import { Sorting, SortingAsc, SortingDesc, Stroke } from '@sds-system/assets';
import { useTheme } from '@sds-system/system';
import type { AllStyledProps, ChildrenProps } from '@sds-system/types';
import type { ChangeEvent } from 'react';
import React, { type ReactNode, useEffect, useState } from 'react';
import { Box } from '../Box';
import { Checkbox } from '../Checkbox';
import { Column } from '../Column';
import { Icon } from '../Icon';
import { Pagination as SDSPagination } from '../Pagination';
import type { PaginationProps } from '../Pagination/Pagination.types';
import { Row } from '../Row';
import { Select, type SelectProps } from '../Select';
import { Typography } from '../Typography';
import { tableLimitSelectDefaultOptions } from './Table.const';
import {
  StyledTable,
  StyledTableBody,
  StyledTableHeader,
  StyledTableRoot,
  StyledTd,
  StyledTh,
  StyledTr,
} from './Table.styled';
import type { TableColumn, TableProps, TableSort } from './Table.types';
import { TableProvider, useTableContext } from './TableContext';

export const Table = <T extends object>({
  size = 'L',
  columns,
  rows,
  styledComponents,
  tablePagination,
  tableLimit,
  tableResult,
  header,
  footer,
  tableScroll,
  tableCheckbox,
  tableSortChange,
  tableException,
  tableKey,
  tableCheckboxId,
  children,
}: TableProps<T>) => {
  const theme = useTheme();
  const [checkedList, setCheckedList] = useState<{ [key: number]: T }>({});
  const [tableSort, setTableSort] = useState<TableSort>({
    sortBy: undefined,
    sortOrder: undefined,
  });
  const isScroll = !!tableScroll?.scrollY || !!tableScroll?.scrollX;
  const checkedListKeys = Object.keys(checkedList);

  useEffect(() => {
    if (tableCheckbox?.checked === undefined) return;
    const newCheckedList: Record<number, T> = {};
    const origin = rows.map((row) => JSON.stringify(row));

    tableCheckbox.checked.forEach((row) => {
      const index = origin.indexOf(JSON.stringify(row));
      newCheckedList[index] = row;
    });

    setCheckedList(newCheckedList);
  }, [tableCheckbox?.checked]);

  const sortChangeHandler = (id: string) => {
    let sortOrder: TableSort['sortOrder'];

    if (tableSort.sortBy === id) {
      sortOrder =
        tableSort.sortOrder === 'asc' ? 'desc' : tableSort.sortOrder === 'desc' ? undefined : 'asc';
    } else {
      sortOrder = 'asc';
    }
    tableSortChange?.(id as keyof T, sortOrder);
    setTableSort({
      sortBy: id as string,
      sortOrder,
    });
  };

  const allCheckboxChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const newCheckboxList = e.target.checked ? rows : {};

    setCheckedList(newCheckboxList);
    tableCheckbox?.onChange(Object.values(newCheckboxList));
  };

  const checkboxChangeHandler = (e: ChangeEvent<HTMLInputElement>, row: T, index: number) => {
    const newCheckedList = { ...checkedList };
    if (e.target.checked) {
      newCheckedList[index] = row;
    } else {
      delete newCheckedList[index];
    }
    setCheckedList(newCheckedList);

    const sortedCheckedList: { [key: number]: T } = {};
    Object.keys(newCheckedList)
      .sort()
      .forEach((key) => {
        sortedCheckedList[Number(key)] = newCheckedList[Number(key)] as T;
      });
    tableCheckbox?.onChange(Object.values(sortedCheckedList));
  };

  const checkboxColumn: TableColumn<{ table_checkbox: boolean }> = {
    id: 'table_checkbox',
    label: (
      <Checkbox
        checkboxSize='XS'
        checked={checkedListKeys.length > 0 && checkedListKeys.length <= Object.keys(rows).length}
        icon={
          checkedListKeys.length > 0 && checkedListKeys.length < Object.keys(rows).length ? (
            <Icon height={2} minHeight={2} minWidth={12} source={Stroke} width={12} />
          ) : undefined
        }
        name={'all_checked' + tableCheckboxId}
        onChange={allCheckboxChangeHandler}
      />
    ),
  };

  const childArray = React.Children.toArray(children);
  const topLayout = childArray.find(
    (child) => React.isValidElement(child) && child.type === Table.TopLayout,
  );
  const bottomLayout = childArray.find(
    (child) => React.isValidElement(child) && child.type === Table.BottomLayout,
  );

  return (
    <TableProvider
      value={{
        tableLimit,
        tablePagination,
      }}
    >
      <StyledTableRoot
        {...styledComponents?.root}
        scrollX={tableScroll?.scrollX}
        scrollY={tableScroll?.scrollY}
      >
        {topLayout ||
          ((header || tableResult || tableLimit) && (
            <Row
              className='table-top-bar'
              css={{ marginRight: 'auto' }}
              mb={20}
              minWidth={0}
              width='100%'
            >
              {tableResult && (
                <Row
                  alignItems='center'
                  className='table-top-bar-left'
                  css={{ flexShrink: 0 }}
                  justifyContent='flex-start'
                >
                  <Typography color='gray.900' fontType='body2.regular'>
                    검색 {tableResult.searchCount}건 / 전체 {tableResult.totalCount}건
                  </Typography>
                </Row>
              )}
              {header}
              {tableLimit && (
                <Row className='table-top-bar-right' css={{ marginLeft: 'auto', flexShrink: 0 }}>
                  <Select
                    className='table-limit'
                    defaultValue={String(tableLimit.limit) || tableLimit?.limitOptions?.[0]?.value}
                    onValueChange={tableLimit.onChangeLimit}
                    options={tableLimit.limitOptions || tableLimitSelectDefaultOptions}
                    size='M'
                  />
                </Row>
              )}
            </Row>
          ))}

        {tableException ? (
          tableException
        ) : (
          <Column
            className='table-wrapper'
            css={{ overflow: 'auto' }}
            maxHeight={'100%'}
            minWidth={0}
            width={'100%'}
          >
            <StyledTable isScroll={isScroll} {...styledComponents?.table}>
              <StyledTableHeader isScroll={isScroll} {...styledComponents?.header}>
                <StyledTr {...styledComponents?.tr}>
                  {tableCheckbox && (
                    <StyledTh
                      key={String(checkboxColumn.id)}
                      size={size}
                      {...tableCheckbox?.styledComponents?.th}
                    >
                      {checkboxColumn.label}
                    </StyledTh>
                  )}
                  {columns?.map((column) => {
                    return (
                      <StyledTh
                        key={String(column.id)}
                        onClick={() => {
                          if (column.sortable) {
                            sortChangeHandler(column.id as string);
                          }
                        }}
                        size={size}
                        {...styledComponents?.th}
                        {...column.styledComponents?.th}
                      >
                        {!column.sortable && column.label}
                        {column.sortable && (
                          <Row
                            alignItems='center'
                            css={{ cursor: 'pointer' }}
                            height='100%'
                            justifyContent='center'
                            width='100%'
                          >
                            {column.label}
                            {(tableSort.sortOrder === undefined ||
                              tableSort.sortBy !== column.id) && (
                              <Icon color='icon.tertiary' ml={8} source={Sorting} />
                            )}
                            {tableSort.sortBy === column.id && tableSort.sortOrder === 'asc' && (
                              <Icon color='primary.400' ml={8} source={SortingAsc} />
                            )}
                            {tableSort.sortBy === column.id && tableSort.sortOrder === 'desc' && (
                              <Icon color='primary.400' ml={8} source={SortingDesc} />
                            )}
                          </Row>
                        )}
                      </StyledTh>
                    );
                  })}
                </StyledTr>
              </StyledTableHeader>
              <StyledTableBody {...styledComponents?.body}>
                {rows?.map((row, index) => {
                  return (
                    <StyledTr
                      key={tableKey ? (row[tableKey] as string) : index}
                      {...styledComponents?.tr}
                    >
                      {tableCheckbox && (
                        <StyledTd
                          key={tableKey ? (row[tableKey] as string) : index}
                          size={size}
                          width={theme.size[size]}
                          {...tableCheckbox.styledComponents?.td}
                        >
                          <Checkbox
                            checked={!!checkedList[index]}
                            name={'table_checkbox' + tableCheckboxId}
                            onChange={(e) => checkboxChangeHandler(e, row, index)}
                            value={index}
                          />
                        </StyledTd>
                      )}
                      {columns?.map((column) => {
                        return (
                          <StyledTd
                            key={String(column.id)}
                            size={size}
                            {...styledComponents?.td}
                            {...column.styledComponents?.td}
                          >
                            {column?.render
                              ? column.render(row[column.id], column.id, row)
                              : (row?.[column.id] as ReactNode)}
                          </StyledTd>
                        );
                      })}
                    </StyledTr>
                  );
                })}
              </StyledTableBody>
            </StyledTable>
          </Column>
        )}
        {footer}
        {bottomLayout ||
          (tablePagination && (
            <Row justifyContent='center' mt={20}>
              <SDSPagination
                current={tablePagination.currentPage}
                onChange={tablePagination.onChangePagination}
                pageSize={tablePagination.pageSize}
                totalPage={tablePagination.totalPage}
              />
            </Row>
          ))}
      </StyledTableRoot>
    </TableProvider>
  );
};

Table.TopLayout = (props: ChildrenProps & AllStyledProps) => {
  const { children, ...styledProps } = props;
  return <Box {...styledProps}>{children}</Box>;
};

Table.BottomLayout = (props: ChildrenProps & AllStyledProps) => {
  const { children, ...styledProps } = props;
  return <Box {...styledProps}>{children}</Box>;
};

Table.Limit = (selectProps: Omit<SelectProps, 'defaultValue' | 'options' | 'onValueChange'>) => {
  const { tableLimit } = useTableContext();

  return (
    <Select
      {...selectProps}
      defaultValue={String(tableLimit?.limit) || tableLimit?.limitOptions?.[0]?.value}
      onValueChange={tableLimit?.onChangeLimit}
      options={tableLimit?.limitOptions || tableLimitSelectDefaultOptions}
    />
  );
};

Table.Pagination = (
  paginationProps: Omit<PaginationProps<'div'>, 'current' | 'pageSize' | 'totalPage' | 'onChange'>,
) => {
  const { tablePagination } = useTableContext();
  if (!tablePagination) return null;

  return (
    <SDSPagination
      {...paginationProps}
      current={tablePagination.currentPage}
      onChange={tablePagination.onChangePagination}
      pageSize={tablePagination.pageSize}
      totalPage={tablePagination.totalPage}
    />
  );
};
